import { useEffect, useState } from 'react';
import { fetchPlans, type Plan } from '../../api/billingApi';

/** The plan catalogue as the server holds it, for any screen that quotes a price.
 *
 *  Prices are never typed into a component: a hard-coded figure stays on screen
 *  after the administrator changes the plan, and on the checkout page it shows a
 *  customer an amount that differs from the one they are charged. `null` until
 *  the catalogue has loaded, so a screen can show a placeholder rather than a
 *  wrong number. */
export function usePlanCatalogue(): Plan[] | null {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  useEffect(() => {
    let live = true;
    fetchPlans().then((p) => { if (live) setPlans(p); }).catch(() => { if (live) setPlans([]); });
    return () => { live = false; };
  }, []);
  return plans;
}

const usd = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const formatMonthly = (p: Plan): string | null => {
  const n = usd(p.monthly_price_usd);
  return n === null ? null : `$${n.toLocaleString('en-US')}/mo`;
};

export const formatAnnual = (p: Plan): string | null => {
  const n = usd(p.annual_price_usd);
  return n === null ? null : `$${n.toLocaleString('en-US')}/yr`;
};

export const findPlan = (plans: Plan[] | null, tier: string): Plan | undefined =>
  plans?.find((p) => p.tier === tier);

/** Plans a customer can buy from an upgrade prompt: visible on the pricing page,
 *  priced (not contact-sales), and not free. Derived, so a plan withdrawn from
 *  sale disappears from every prompt at once. */
export const sellablePlans = (plans: Plan[] | null): Plan[] =>
  (plans ?? []).filter((p) => p.show_on_pricing && !p.contact_sales && (usd(p.monthly_price_usd) ?? 0) > 0);

/** Percentage saved by paying annually, from the catalogue. The server works it
 *  out from the two prices, so a changed price changes the figure everywhere. */
export const savingPct = (p: Plan | undefined): number | null => {
  if (!p) return null;
  if (typeof p.annual_savings_pct === 'number') return p.annual_savings_pct;
  const m = usd(p.monthly_price_usd), a = usd(p.annual_price_usd);
  if (m === null || a === null || m <= 0) return null;
  return Math.round((1 - a / (m * 12)) * 100);
};

/** Months given free by paying a year up front (12 minus the months charged). */
export const monthsFree = (p: Plan | undefined): number | null => {
  const m = usd(p?.monthly_price_usd), a = usd(p?.annual_price_usd);
  if (m === null || a === null || m <= 0) return null;
  return Math.max(0, Math.round(12 - a / m));
};

/** The best annual saving across the plans a customer is offered, for one badge. */
export const bestSavingPct = (plans: Plan[] | null): number | null => {
  const values = sellablePlans(plans).map(savingPct).filter((v): v is number => v !== null);
  return values.length ? Math.max(...values) : null;
};
